import React, { useEffect, useMemo, useCallback } from "react";
import { Typography } from "@mui/material";
import { Icon, Button, styled } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

// Components
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";

// Redux
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector, useDispatch } from "react-redux"; // useDispatch

// Airport Retrieval
import { IPassengerOverview, IFilterOption } from "@vmosolutions/oc-interfaces";
import _ from "lodash";

// Styling
import classNames from "classnames";

import { ISearchStatus, defaultSearchStatus } from "./Helpers";
import { overviewFilterOptions, filterEvents } from "./CharterOverviewHelper";

// Airport Retrieval - used for Base ICAO filter.
import { airportSearchString, updateAirportOptions, formatIdentMultipleAirports } from "src/utils/airportHelper"; //findAirports, filterAirports
import { IStructuresState, IAirportOption } from "src/redux/reducers/StructuresReducer"; //IAirportOptionMap

interface IEventFilter {
  dataType?: string;
  dataInput?: any; // incoming event dataset
  dataOutput: (data: any) => any; // returns filtered dataset
  allowMultiFilter?: boolean;
  parentElement?: any;
  hideLabel?: boolean;
  searchStatus?: (status: ISearchStatus) => void;
  noFilter?: boolean;
  showDateRangeRRule?: boolean;
  //mode?: 'filter'|'search'
}

const OCCharterOverviewFilter = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  "&.saved_search_selection": {
    marginLeft: 10,
    width: "auto",
    "& .saved_search_icon": {
      position: "relative",
      width: 24,
      height: 24,
      "& .search_icon": {},
      "& .star_icon": {
        width: 9,
        height: 10,
        top: 4,
        left: 5,
        position: "absolute",
      },
    },
    "& .timeline_event_saved_search_filter": {
      width: 263,
    },
  },
  "& .filter_drop_down_menu": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    margin: "5px 10px",
    alignItems: "flex-start",
    "& .timeline_event_search_filter": {
      width: 263,
    },
  },
  "& .active_filters": {
    "& .filter": { width: 263 },
    display: "flex",
    marginLeft: 10,
    alignItems: "flex-start",
    flexWrap: "wrap",
    "& .date_range_filter": {
      display: "flex",
      width: 263,
      flexDirection: "column",
      margin: 5,
      "& .from_date, & .to_date": {
        margin: 0,
        "& label": { width: 68 },
      },
      "& .daysofweek_selection": {
        margin: 5,
        "& .input_control label": { width: 68, margin: "0px 10px auto auto" },
        "& .react-select-container": { width: 175 },
      },
    },
    "& button.search_button": {
      margin: 3,
      padding: "2px 8px",
      fontSize: 12,
      fontWeight: 300,
      width: 100,
      "& .search_icon": { fontSize: 18, marginRight: 5 },
    },
    "& .results_match_filters": {
      margin: 3,
      borderRadius: 3,
      width: 26,
      height: 26,
      border: `1px solid ${theme.palette.success.main}`,
      "& path": { fill: theme.palette.success.main },
    },
  },
}));

/**
 * CharterOverviewFilter component handles the ability to search content
 * @param props -
 */
export const CharterOverviewFilter = (props: IEventFilter): JSX.Element => {
  const dispatch = useDispatch();
  const { dataType, dataInput, dataOutput, parentElement, hideLabel, allowMultiFilter, searchStatus } = props;

  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions } = structures;

  // Store Events loaded by Database Search
  // const [databaseResults, setDatabaseResults] = React.useState<ISearchPairing[]>([]);
  const [searching, setSearching] = React.useState<boolean>(false);

  // Store filtered events
  const [filteredEvents, setFilteredEvents] = React.useState<IPassengerOverview[]>(dataInput);
  const [selectedFilter, setSelectedFilter] = React.useState<IFilterOption[]>([]);

  // Make sure all filters contain a value before enabling the search button.
  const enableSearch = useMemo(() => {
    let searchValid = true;
    selectedFilter?.forEach((filter) => {
      if (!filter.searchValue || (Array.isArray(filter.searchValue) && filter.searchValue?.length === 0)) {
        searchValid = false; // set search as invalid if any filters have no assigned value.
      }
    });
    return searchValid;
  }, [selectedFilter]);

  // Clear all filters and results. (resets the tool)
  const handleClearAll = useCallback(() => {
    // setSearchFilter([]);
    setSelectedFilter([]);
    setFilteredEvents([]);
  }, []);

  // If data type changes externally , reset tool.
  useEffect(() => {
    handleClearAll();
  }, [dataType, handleClearAll]);

  const filterResults = useCallback(
    (selectedFilter: any): void => {
      setSearching(true);
      const searchResults: IPassengerOverview[] = filterEvents(selectedFilter, dataInput);
      setSearching(false);
      setFilteredEvents(searchResults);
    },
    [dataInput]
  );

  // If filtered events change, update parent.
  useEffect(() => {
    if (dataOutput) dataOutput(filteredEvents);
  }, [filteredEvents, dataOutput]);

  const selectionGroupedOptions = useMemo(() => {
    const groupArray = [];
    groupArray.push({
      label: "Overview Fields",
      options: overviewFilterOptions,
    });
    return groupArray;
  }, []);

  /**
   * Accepts the user's input & determines the type of the user's input.
   * @param event -
   */
  const handleSearch = (key: string, event: any): void => {
    const newFilter = [...selectedFilter];
    const searchValue = !!event && !!event.target ? event.target.value : event;
    switch (key) {
      case "flightNumber": {
        const numberArray = searchValue
          ? searchValue.split(",").map((flightNumber: string) => flightNumber.trim())
          : null;
        const numberArrayfilter = newFilter.find((filter: IFilterOption) => filter.value.includes(key));
        if (numberArrayfilter) {
          numberArrayfilter.searchValue = numberArray;
        }
        break;
      }
      case "origin":
      case "destination": {
        const locArrayFilter = newFilter.find((filter: IFilterOption) => filter.value.includes(key));
        if (locArrayFilter) {
          locArrayFilter.searchValue = searchValue;
        }
        break;
      }
      default: {
        const filter = newFilter.find((filter: IFilterOption) => filter.value.includes(key));
        if (filter) {
          filter.searchValue = searchValue;
        }
      }
    }
    searchStatus(defaultSearchStatus);
    setSelectedFilter(newFilter);
  };
  useEffect(() => {
    filterResults(selectedFilter);
  }, [filterResults, selectedFilter]);
  /**
   * Handles the user's selected filter to search for a flight.
   * @param selectedFilter -
   */
  const handleSelectedFilter = (newFilters: any): void => {
    const filterArray = !newFilters
      ? []
      : Array.isArray(newFilters)
        ? _.cloneDeep(newFilters)
        : [_.cloneDeep(newFilters)];
    // Carry over any currently selected filter values (required for using multiple filters)
    if (filterArray.length > 0) {
      filterArray.map((newF: IFilterOption) => {
        const filter = selectedFilter.find((oldF: IFilterOption) => oldF.value === newF.value);
        if (!!filter && !!filter.searchValue) {
          newF.searchValue = filter.searchValue;
        }
        return false;
      });
    }
    searchStatus(defaultSearchStatus);
    setSelectedFilter(filterArray);
  };
  /**
   * Maps out selected filter array and returns the selected field inputs if selected.
   */
  const renderFilterInputs = (): JSX.Element[] => {
    if (!selectedFilter || selectedFilter.length === 0) {
      return null;
    }

    return selectedFilter.map((filter: IFilterOption, index: number) => {
      const { value, label } = filter;
      const stringTypes = ["firstName", "lastName", "flightNumber"];
      const flightTypes = ["origin", "destination"];
      const key = value;

      if (stringTypes.includes(key)) {
        return (
          <div key={index}>
            <TextField
              className="auto_width filter table_filter_textfield"
              label={label}
              name={key}
              value={filter.searchValue}
              onChange={(event: any) => handleSearch(key, event)}
              // placeholder=""
              // multiline={true}
              // minRows={2}
              // maxRows={4}
            />
          </div>
        );
      } else if (flightTypes.includes(key)) {
        return (
          <div key={index}>
            <SelectField
              className="auto_width filter airport_selection"
              label={label}
              name={key}
              value={formatIdentMultipleAirports(filter, Object.values(airportOptions))}
              isMulti={true}
              onChange={(airports: IAirportOption[]) => handleSearch(key, airports)}
              onInputChange={(ident: string) => updateAirportOptions(ident, airportOptions, dispatch)}
              options={Object.values(airportOptions)}
              getOptionLabel={(airport: IAirportOption) =>
                airport ? `${airport.ident}/${airport.iata} - ${airport.name}` : ""
              }
              getOptionValue={(airport: IAirportOption) => airport.ident}
              noOptionsMessage={() => airportSearchString}
              isSearchable={true}
              isClearable={true}
              menuPosition="fixed"
              maxMenuHeight={400}
              menuPortalTarget={parentElement}
              openMenuOnFocus={false}
              blurInputOnSelect={true}
              closeMenuOnSelect={true}
              placeholder=""
            />
          </div>
        );
      }
      return null;
    });
  };

  return (
    <OCCharterOverviewFilter className={classNames("search_filters")}>
      {!hideLabel && (
        <Typography variant="subtitle1" className="body_text">
          Find Content by:
        </Typography>
      )}

      <div className="filter_drop_down_menu">
        {hideLabel && <Icon className="control_icon">filter_list</Icon>}
        <SelectField
          className="timeline_event_search_filter"
          label=""
          name="advanced_search_filters"
          value={selectedFilter}
          options={selectionGroupedOptions}
          onChange={handleSelectedFilter}
          isMulti={allowMultiFilter}
          isClearable={true}
          menuPortalTarget={parentElement}
          menuPosition="fixed"
          maxMenuHeight={400}
          multiMode={allowMultiFilter ? "checkbox" : null}
          placeholder={
            selectedFilter?.length === 0
              ? "Select Filters..."
              : selectedFilter?.length === 1
                ? selectedFilter[0].label
                : "Multiple Filters Selected..."
          }
        />
      </div>

      {selectedFilter?.length > 0 && (
        <div className="active_filters">
          {renderFilterInputs()}

          <Button
            id={"search_btn"}
            variant="outlined"
            onClick={() => filterResults(selectedFilter)}
            disabled={!enableSearch || searching}
            className="search_button"
          >
            {!searching ? (
              <>
                <Icon className="search_icon">search</Icon>Filter
              </>
            ) : (
              <>
                <CircularProgress color="inherit" style={{ width: 14, height: 14, marginRight: 8 }} /> Filter
              </>
            )}
          </Button>
        </div>
      )}
    </OCCharterOverviewFilter>
  );
};
